import {
    useEffect,
    useState,
} from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
    ORGANIZATION_MASTERS,
} from "./constants";

import {
    createOrganization,
    updateOrganization,
    getOrganizations,
} from "services/api/organizationApi";

import {
    showSuccess,
    showError,
} from "utils/toast";


const INITIAL_FORM = {
    name: "",
    code: "",
    parentId: "",
    status: "active",
};


export default function OrganizationModal({
    open,
    onClose,
    reload,
    master,
    item,
}) {

    const isEdit =
        Boolean(item);


    const currentMaster =
        ORGANIZATION_MASTERS.find(
            (m) =>
                m.value ===
                master
        );


    const hasParent =
        currentMaster?.hasParent ||
        false;


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        parents,
        setParents,
    ] = useState([]);


    const [
        form,
        setForm,
    ] = useState(
        INITIAL_FORM
    );


    // =========================================================
    // INPUT
    // =========================================================

    const handleChange = (
        field,
        value
    ) => {

        setForm(
            (prev) => ({
                ...prev,
                [field]:
                    value,
            })
        );

    };


    // =========================================================
    // PARENT LABEL
    // =========================================================

    const getParentLabel = () => {

        if (
            !currentMaster?.parent
        ) {
            return "Parent";
        }


        const parentMaster =
            ORGANIZATION_MASTERS.find(
                (item) =>
                    item.value ===
                    currentMaster.parent
            );


        return (
            parentMaster?.label ||
            "Parent"
        );

    };


    // =========================================================
    // GET PARENT ID FROM RECORD
    // =========================================================
    //
    // Supports both:
    //
    //   item.countryId
    //   item.country.id
    //
    // This is important because list APIs return nested
    // parent objects.
    // =========================================================

    const getParentId = (
        record
    ) => {

        if (!record) {
            return "";
        }


        switch (master) {

            // -------------------------------------------------
            // COUNTRY
            // Country → Business Region
            // -------------------------------------------------

            case "countries":

                return (
                    record.regionId ||
                    record.region?.id ||
                    ""
                );


            // -------------------------------------------------
            // STATE
            // State → Country
            // -------------------------------------------------

            case "states":

                return (
                    record.countryId ||
                    record.country?.id ||
                    ""
                );


            // -------------------------------------------------
            // DISTRICT
            // District → State
            // -------------------------------------------------

            case "districts":

                return (
                    record.stateId ||
                    record.state?.id ||
                    ""
                );


            // -------------------------------------------------
            // CITY
            // City → District
            // -------------------------------------------------

            case "cities":

                return (
                    record.districtId ||
                    record.district?.id ||
                    ""
                );


            // -------------------------------------------------
            // ZONE
            // Zone → City
            // -------------------------------------------------

            case "zones":

                return (
                    record.cityId ||
                    record.city?.id ||
                    ""
                );


            // -------------------------------------------------
            // AREA
            // Area → Zone
            // -------------------------------------------------

            case "areas":

                return (
                    record.zoneId ||
                    record.zone?.id ||
                    ""
                );


            // -------------------------------------------------
            // BEAT
            // Beat → Area
            // -------------------------------------------------

            case "beats":

                return (
                    record.areaId ||
                    record.area?.id ||
                    ""
                );


            // -------------------------------------------------
            // TERRITORY
            // Territory → Beat
            // -------------------------------------------------

            case "territories":

                return (
                    record.beatId ||
                    record.beat?.id ||
                    ""
                );


            // -------------------------------------------------
            // HEADQUARTER
            // Headquarter → City
            // -------------------------------------------------

            case "headquarters":

                return (
                    record.cityId ||
                    record.city?.id ||
                    ""
                );


            // -------------------------------------------------
            // WORKING STATION
            // Working Station → City
            // -------------------------------------------------

            case "working-stations":

                return (
                    record.cityId ||
                    record.city?.id ||
                    ""
                );


            // -------------------------------------------------
            // STAY POINT
            // Stay Point → City
            // -------------------------------------------------

            case "stay-points":

                return (
                    record.cityId ||
                    record.city?.id ||
                    ""
                );


            default:

                return (
                    record.parentId ||
                    ""
                );
        }

    };


    // =========================================================
    // LOAD PARENT RECORDS
    // =========================================================

    const loadParents = async () => {

        if (
            !hasParent ||
            !currentMaster?.parent
        ) {

            setParents([]);

            return;

        }


        try {

            const response =
                await getOrganizations(
                    currentMaster.parent,
                    1,
                    1000,
                    ""
                );


            /*
            |--------------------------------------------------------------------------
            | Supported API response formats
            |--------------------------------------------------------------------------
            */

            const records =
                response?.data?.items ||
                response?.data?.records ||
                response?.data?.regions ||
                response?.items ||
                response?.records ||
                response?.regions ||
                [];


            setParents(
                Array.isArray(records)
                    ? records
                    : []
            );

        } catch (err) {

            console.error(
                "Parent load error:",
                err
            );


            setParents([]);

            showError(
                err?.response?.data?.message ||
                "Unable to load parent list."
            );

        }

    };


    // =========================================================
    // OPEN / EDIT
    // =========================================================

    useEffect(() => {

        if (!open) {
            return;
        }


        loadParents();


        if (isEdit) {

            const parentId =
                getParentId(item);


            setForm({

                name:
                    item?.name ||
                    "",

                code:
                    item?.code ||
                    "",

                parentId:
                    parentId !== null &&
                        parentId !== undefined
                        ? String(
                            parentId
                        )
                        : "",

                status:
                    item?.status ||
                    "active",

            });

        } else {

            setForm({
                ...INITIAL_FORM,
            });

        }

    }, [
        open,
        master,
        item,
    ]);


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit =
        async (e) => {

            e.preventDefault();


            // -------------------------------------------------
            // NAME
            // -------------------------------------------------

            if (
                !form.name.trim()
            ) {

                showError(
                    master ===
                        "business-regions"
                        ? "Region name is required."
                        : "Name is required."
                );

                return;

            }


            // -------------------------------------------------
            // CODE
            // -------------------------------------------------

            if (
                !form.code.trim()
            ) {

                showError(
                    master ===
                        "business-regions"
                        ? "Region code is required."
                        : "Code is required."
                );

                return;

            }


            // -------------------------------------------------
            // PARENT
            // -------------------------------------------------

            if (
                hasParent &&
                !form.parentId
            ) {

                showError(
                    `Please select ${getParentLabel()}.`
                );

                return;

            }


            try {

                setLoading(
                    true
                );


                // -------------------------------------------------
                // BASE PAYLOAD
                // -------------------------------------------------

                const payload = {

                    name:
                        form.name
                            .trim(),

                    code:
                        form.code
                            .trim()
                            .toUpperCase(),

                    status:
                        form.status,

                };


                // =================================================
                // PARENT MAPPING
                // =================================================

                switch (master) {

                    // ---------------------------------------------
                    // Business Region
                    // No parent
                    // ---------------------------------------------

                    case "business-regions":

                        break;


                    // ---------------------------------------------
                    // Country → Business Region
                    // ---------------------------------------------

                    case "countries":

                        payload.regionId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // State → Country
                    // ---------------------------------------------

                    case "states":

                        payload.countryId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // District → State
                    // ---------------------------------------------

                    case "districts":

                        payload.stateId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // City → District
                    // ---------------------------------------------

                    case "cities":

                        payload.districtId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // Zone → City
                    // ---------------------------------------------

                    case "zones":

                        payload.cityId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // Area → Zone
                    // ---------------------------------------------

                    case "areas":

                        payload.zoneId =
                            form.parentId;

                        /*
                        |--------------------------------------------------------------------------
                        | Preserve direct city support when editing
                        | an area returned with cityId.
                        |--------------------------------------------------------------------------
                        */

                        if (item?.cityId) {

                            payload.cityId =
                                String(
                                    item.cityId
                                );

                        }

                        break;


                    // ---------------------------------------------
                    // Beat → Area
                    // ---------------------------------------------

                    case "beats":

                        payload.areaId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // Territory → Beat
                    // ---------------------------------------------

                    case "territories":

                        payload.beatId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // Headquarter → City
                    // ---------------------------------------------

                    case "headquarters":

                        payload.parentId =
                            form.parentId;

                        /*
                        |--------------------------------------------------------------------------
                        | Backend create/update accepts parentId
                        | and maps it to cityId.
                        |--------------------------------------------------------------------------
                        */

                        break;


                    // ---------------------------------------------
                    // Working Station → City
                    // ---------------------------------------------

                    case "working-stations":

                        payload.parentId =
                            form.parentId;

                        break;


                    // ---------------------------------------------
                    // Stay Point → City
                    // ---------------------------------------------

                    case "stay-points":

                        payload.parentId =
                            form.parentId;

                        break;


                    default:

                        if (
                            hasParent
                        ) {

                            payload.parentId =
                                form.parentId;

                        }

                        break;

                }


                // =================================================
                // CREATE / UPDATE
                // =================================================

                if (
                    isEdit
                ) {

                    await updateOrganization(
                        master,
                        item.id,
                        payload
                    );


                    showSuccess(
                        `${currentMaster?.label} updated successfully.`
                    );

                } else {

                    await createOrganization(
                        master,
                        payload
                    );


                    showSuccess(
                        `${currentMaster?.label} created successfully.`
                    );

                }


                // =================================================
                // RELOAD
                // =================================================

                await reload();


                // =================================================
                // RESET
                // =================================================

                setForm({
                    ...INITIAL_FORM,
                });


                onClose();

            } catch (
            error
            ) {

                console.error(
                    "Organization save error:",
                    error
                );


                showError(
                    error?.response?.data?.message ||
                    "Something went wrong."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // =========================================================
    // CLOSED
    // =========================================================

    if (!open) {
        return null;
    }


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <CrudModal

            open={
                open
            }


            title={
                isEdit
                    ? `Edit ${currentMaster?.label}`
                    : `Add ${currentMaster?.label}`
            }


            loading={
                loading
            }


            submitText={
                isEdit
                    ? "Update"
                    : "Save"
            }


            onClose={() => {

                setForm({
                    ...INITIAL_FORM,
                });

                setParents([]);

                onClose();

            }}


            onSubmit={
                handleSubmit
            }

        >

            <div className="grid grid-cols-2 gap-5">


                {/* =================================================
                    NAME
                ================================================= */}

                <CrudInput

                    label={
                        master ===
                            "business-regions"
                            ? "Region Name"
                            : "Name"
                    }


                    required


                    value={
                        form.name
                    }


                    onChange={
                        (e) =>
                            handleChange(
                                "name",
                                e.target.value
                            )
                    }

                />


                {/* =================================================
                    CODE
                ================================================= */}

                <CrudInput

                    label={
                        master ===
                            "business-regions"
                            ? "Region Code"
                            : "Code"
                    }


                    required


                    value={
                        form.code
                    }


                    onChange={
                        (e) =>
                            handleChange(
                                "code",
                                e.target.value
                            )
                    }

                />


                {/* =================================================
                    PARENT
                ================================================= */}

                {hasParent && (

                    <CrudSelect

                        label={
                            getParentLabel()
                        }


                        required


                        value={
                            String(
                                form.parentId ||
                                ""
                            )
                        }


                        onChange={
                            (e) =>
                                handleChange(
                                    "parentId",
                                    e.target.value
                                )
                        }


                        options={
                            [
                                {
                                    value:
                                        "",
                                    label:
                                        `Select ${getParentLabel()}`,
                                },

                                ...parents.map(
                                    (
                                        parent
                                    ) => ({

                                        value:
                                            String(
                                                parent.id
                                            ),

                                        label:
                                            parent.name,

                                    })
                                ),
                            ]
                        }

                    />

                )}


                {/* =================================================
                    STATUS
                ================================================= */}

                <CrudSelect

                    label="Status"


                    value={
                        form.status
                    }


                    onChange={
                        (e) =>
                            handleChange(
                                "status",
                                e.target.value
                            )
                    }


                    options={[
                        {
                            value:
                                "active",
                            label:
                                "Active",
                        },

                        {
                            value:
                                "inactive",
                            label:
                                "Inactive",
                        },
                    ]}

                />

            </div>

        </CrudModal>

    );

}